// hsbakrad.js
import React, { useState, useEffect, useRef } from "react";
import { Wheel } from "react-custom-roulette";
import "./hsbakrad.css";
import DefaultTemplates from "../../components/DefaultTemplates";

export default function HSBakRad() {
  // Wheel segments
  const [data, setData] = useState([
    { option: "1 Bak", style: { backgroundColor: "#FF3E3E", textColor: "#ffffff" } },
    { option: "2 Bak", style: { backgroundColor: "#00A1FF", textColor: "#ffffff" } },
    { option: "1 Shot", style: { backgroundColor: "#FFD700", textColor: "#ffffff" } },
    { option: "2 Shot", style: { backgroundColor: "#9C27B0", textColor: "#ffffff" } },
    { option: "6 Slokken", style: { backgroundColor: "#4CAF50", textColor: "#ffffff" } },
  ]);

  // Spin state
  const [mustSpin, setMustSpin] = useState(false);
  const [prizeNumber, setPrizeNumber] = useState(0);

  // Name‐list UI
  const [newName, setNewName] = useState("");
  const [editIndex, setEditIndex] = useState(null);
  const [editValue, setEditValue] = useState("");

  // Template UI & storage
  const [templates, setTemplates] = useState([]);
  const [showTemplates, setShowTemplates] = useState(false);
  const [templateName, setTemplateName] = useState("");
  
  // Result notification
  const [showResult, setShowResult] = useState(false);
  const [resultValue, setResultValue] = useState("");
  
  // Confirmation dialogs
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [confirmationAction, setConfirmationAction] = useState(null);
  const [confirmationMessage, setConfirmationMessage] = useState("");
  
  // Refs for keyboard navigation
  const newNameInputRef = useRef(null);
  const templateNameInputRef = useRef(null);
  const spinButtonRef = useRef(null);
  const closeResultButtonRef = useRef(null);
  const confirmYesButtonRef = useRef(null);
  const confirmNoButtonRef = useRef(null);

  // Load templates from localStorage once
  useEffect(() => {
    const stored = localStorage.getItem("hsbakrad_templates");
    if (stored) setTemplates(JSON.parse(stored));
  }, []);
  
  // Add keyboard event listeners for modals and dialogs
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Handle Escape key for closing modals
      if (e.key === 'Escape') {
        if (showResult) {
          closeResult();
        } else if (showConfirmation) {
          handleConfirmNo();
        }
      }
      
      // Handle Enter key for confirmation dialog
      if (e.key === 'Enter' && showConfirmation) {
        handleConfirmYes();
      }
      
      // Focus management for modals
      if (showResult && closeResultButtonRef.current && !document.activeElement.closest('.result-notification')) {
        closeResultButtonRef.current.focus();
      }
      
      if (showConfirmation && confirmYesButtonRef.current && !document.activeElement.closest('.confirmation-dialog')) {
        confirmYesButtonRef.current.focus();
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [showResult, showConfirmation]);
  
  // Handle selecting a default template
  const handleSelectDefaultTemplate = (template) => {
    setData(template.segments);
    // Removed popup notification
  };

  // Helpers for text sizing
  function getMaxNameLength(count) {
    if (count <= 8) return 20;
    if (count <= 12) return 15;
    return 10;
  }
  function truncateName(name, max) {
    return name.length > max ? name.slice(0, max - 3) + "…" : name;
  }
  const maxNameLength = getMaxNameLength(data.length);
  const longestNameLength = data.reduce(
    (m, i) => Math.max(m, truncateName(i.option, maxNameLength).length),
    0
  );
  let fontSize = 24;
  if (longestNameLength > 15) fontSize = 18;
  if (longestNameLength > 18) fontSize = 15;

  // Spin handlers
  const handleSpinClick = () => {
    if (!data.length || mustSpin) return; // Prevent spinning if already spinning
    const newIdx = Math.floor(Math.random() * data.length);
    setPrizeNumber(newIdx);
    setMustSpin(true);
  };
  const handleStopSpinning = () => {
    setMustSpin(false);
    setResultValue(data[prizeNumber].option);
    setShowResult(true);
    
    // We'll remove the audio for now since it's causing issues
    // If you want to add sound later, you can host an audio file in your project
    // and reference it with a relative path
  };
  
  const closeResult = () => {
    setShowResult(false);
  };
  
  // Confirmation dialog handlers
  const handleConfirmYes = () => {
    if (confirmationAction) {
      confirmationAction();
    }
    setShowConfirmation(false);
    setConfirmationAction(null);
  };
  
  const handleConfirmNo = () => {
    setShowConfirmation(false);
    setConfirmationAction(null);
  };

  // Name‐list handlers
  const handleAddName = (e) => {
    e.preventDefault();
    if (!newName.trim() || mustSpin) return; // Prevent adding while spinning
    const pool = ["#FF3E3E", "#00A1FF", "#FFD700", "#9C27B0", "#4CAF50", "#0071e3", "#2A4759"];
    const bg = pool[data.length % pool.length];
    const textColor = bg === "#FFD700" ? "#000000" : "#ffffff";
    setData([...data, { option: newName.trim(), style: { backgroundColor: bg, textColor } }]);
    setNewName("");
    
    // Focus back on the input for quick consecutive additions
    if (newNameInputRef.current) {
      newNameInputRef.current.focus();
    }
  };
  
  // Handle key press in the add name form
  const handleAddNameKeyDown = (e) => {
    if (e.key === 'Enter' && newName.trim() && !mustSpin) {
      handleAddName(e);
    }
  };
  const handleClear = () => {
    if (mustSpin) return; // Prevent clearing while spinning
    setConfirmationMessage("Weet je zeker dat je alle namen wilt wissen?");
    setConfirmationAction(() => () => setData([]));
    setShowConfirmation(true);
  };
  const handleEdit = (i) => {
    if (mustSpin) return; // Prevent editing while spinning
    setEditIndex(i); 
    setEditValue(data[i].option);
  };
  const handleEditSave = (i) => {
    if (!editValue.trim() || mustSpin) return; // Prevent saving while spinning
    const copy = [...data]; 
    copy[i].option = editValue.trim();
    setData(copy); 
    setEditIndex(null); 
    setEditValue("");
  };
  const handleEditCancel = () => {
    if (mustSpin) return; // Prevent canceling while spinning
    setEditIndex(null); 
    setEditValue("");
  };
  const handleDelete = (i) => {
    if (mustSpin) return; // Prevent deleting while spinning
    setConfirmationMessage(`Weet je zeker dat je "${data[i].option}" wilt verwijderen?`);
    setConfirmationAction(() => () => {
      setData(data.filter((_, idx) => idx !== i));
      if (editIndex === i) handleEditCancel();
    });
    setShowConfirmation(true);
  };

  // Template handlers (persisted in localStorage)
  const handleSaveTemplate = () => {
    if (!templateName.trim() || mustSpin) return; // Prevent saving while spinning
    const newTpl = { name: templateName.trim(), segments: data };
    const updated = [...templates, newTpl];
    setTemplates(updated);
    localStorage.setItem("hsbakrad_templates", JSON.stringify(updated));
    setTemplateName("");
    
    // Focus back on the template name input for better UX
    if (templateNameInputRef.current) {
      templateNameInputRef.current.focus();
    }
  };
  
  // Handle key press in the template name input
  const handleTemplateNameKeyDown = (e) => {
    if (e.key === 'Enter' && templateName.trim() && !mustSpin) {
      e.preventDefault();
      handleSaveTemplate();
    }
  };
  const handleLoadTemplate = (tpl) => {
    if (mustSpin) return; // Prevent loading while spinning
    setData(tpl.segments);
    setShowTemplates(false);
  };
  const handleDeleteTemplate = (idx) => {
    if (mustSpin) return; // Prevent deleting while spinning
    setConfirmationMessage(`Weet je zeker dat je template "${templates[idx].name}" wilt verwijderen?`);
    setConfirmationAction(() => () => {
      const updated = templates.filter((_, i) => i !== idx);
      setTemplates(updated);
      localStorage.setItem("hsbakrad_templates", JSON.stringify(updated));
    });
    setShowConfirmation(true);
  };

  // Removed confetti function

  return (
    <div className="hsbakrad-page">
      {/* Result notification */}
      <div 
        className={`result-notification-overlay ${showResult ? 'visible' : ''}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby="result-title"
      >
        <div className="result-notification">
          <h2 className="result-title" id="result-title">Resultaat!</h2>
          <div 
            className="result-value" 
            style={{
              backgroundColor: data[prizeNumber]?.style?.backgroundColor || 'rgba(0, 113, 227, 0.2)',
              color: data[prizeNumber]?.style?.textColor || '#fff'
            }}
          >
            {resultValue}
          </div>
          <button 
            className="close-result-btn" 
            onClick={closeResult}
            ref={closeResultButtonRef}
            aria-label="Sluit resultaat"
          >
            Sluiten
          </button>
        </div>
      </div>
      
      {/* Confirmation dialog */}
      <div 
        className={`confirmation-overlay ${showConfirmation ? 'visible' : ''}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby="confirmation-message"
      >
        <div className="confirmation-dialog">
          <p className="confirmation-message" id="confirmation-message">{confirmationMessage}</p>
          <div className="confirmation-buttons">
            <button 
              className="confirm-yes-btn" 
              onClick={handleConfirmYes}
              ref={confirmYesButtonRef}
            >
              Ja
            </button>
            <button 
              className="confirm-no-btn" 
              onClick={handleConfirmNo}
              ref={confirmNoButtonRef}
            >
              Nee
            </button>
          </div>
        </div>
      </div>
      
      <h1 className="hsbakrad-header">HS Bak Rad</h1>
      <div className="hsbakrad-main-row">
        {/* Wheel */}
        <div className="hsbakrad-wheel-container">
          {data.length > 0 ? (
            <div className="wheel-click-wrapper" onClick={handleSpinClick}>
              <Wheel
                mustStartSpinning={mustSpin}
                prizeNumber={prizeNumber}
                data={data.map(item => ({
                  option: truncateName(item.option, maxNameLength),
                  style: item.style
                }))}
                backgroundColors={["#ffffff", "#eeeeee"]}
                textColors={["#ffffff"]}
                outerBorderColor="#0071e3"
                outerBorderWidth={10}
                innerBorderColor="#00A1FF"
                innerBorderWidth={6}
                radiusLineColor="#ffffff"
                radiusLineWidth={4}
                perpendicularText={false}
                onStopSpinning={handleStopSpinning}
                spinDuration={1.2}
                fontSize={fontSize}
                fontWeight={700}
                textDistance={60}
              />
            </div>
          ) : (
            <div className="empty-wheel">
              <div className="empty-wheel-message">Voeg namen toe</div>
            </div>
          )}
        </div>

        {/* Name list */}
        <div className="hsbakrad-namelist-container">
          <h2 className="namelist-title">De inhoud</h2>
          <ul className="hsbakrad-namelist">
            {data.map((item, i) => (
              <li key={i} className="namelist-item">
                {editIndex === i ? (
                  <>
                    <input
                      className="namelist-edit-input"
                      value={editValue}
                      onChange={e => !mustSpin && setEditValue(e.target.value)}
                      disabled={mustSpin}
                      autoFocus
                    />
                    <button className="namelist-btn save" onClick={() => handleEditSave(i)} disabled={mustSpin}>Opslaan</button>
                    <button className="namelist-btn cancel" onClick={handleEditCancel} disabled={mustSpin}>Annuleer</button>
                  </>
                ) : (
                  <>
                    <span
                      className="namelist-name"
                      style={{
                        background: item.style.backgroundColor,
                        color: item.style.textColor
                      }}
                    >
                      {item.option}
                    </span>
                    <button className="namelist-btn edit" onClick={() => handleEdit(i)} disabled={mustSpin}>Bewerk</button>
                    <button className="namelist-btn delete" onClick={() => handleDelete(i)} disabled={mustSpin}>Verwijder</button>
                  </>
                )}
              </li>
            ))}
          </ul>
          
          {/* Controls moved inside namelist container */}
          <div className="namelist-controls">
            <form className="add-name-form" onSubmit={handleAddName}>
              <input
                type="text"
                placeholder="Nieuwe naam"
                value={newName}
                onChange={e => !mustSpin && setNewName(e.target.value)}
                onKeyDown={handleAddNameKeyDown}
                disabled={mustSpin}
                className="add-name-input"
                ref={newNameInputRef}
                aria-label="Voer een nieuwe naam in"
              />
              <button 
                type="submit" 
                className="add-name-btn" 
                disabled={mustSpin}
                aria-label="Voeg naam toe aan het rad"
              >
                Toevoegen
              </button>
            </form>

            <div className="hsbakrad-action-buttons">
              <button 
                onClick={handleSpinClick} 
                className={`spin-btn ${mustSpin ? 'spinning' : ''}`} 
                disabled={!data.length || mustSpin}
                ref={spinButtonRef}
                aria-label={mustSpin ? 'Rad draait...' : 'Draai het rad'}
              >
                {mustSpin ? 'Spinning...' : 'Spin!'}
              </button>
              <button 
                onClick={handleClear} 
                className="clear-btn" 
                disabled={mustSpin}
                aria-label="Wis alle namen van het rad"
              >
                Wis alles
              </button>
            </div>
            
            {/* Template controls moved inside namelist container */}
            <div className="template-section">
              <div className="template-controls">
                <div className="template-input-group">
                  <input
                    type="text"
                    placeholder="Template naam"
                    value={templateName}
                    onChange={e => !mustSpin && setTemplateName(e.target.value)}
                    onKeyDown={handleTemplateNameKeyDown}
                    disabled={mustSpin}
                    className="template-input"
                    ref={templateNameInputRef}
                    aria-label="Voer een naam in voor je template"
                  />
                  <button 
                    className="template-btn save" 
                    onClick={handleSaveTemplate}
                    disabled={mustSpin}
                    aria-label="Sla huidige rad op als template"
                  >
                    Opslaan
                  </button>
                </div>
                <div className="template-toggle-container">
                  <button
                    className="template-btn load"
                    onClick={() => {
                      if (mustSpin) return;
                      setShowTemplates(!showTemplates);
                    }}
                    disabled={mustSpin}
                  >
                    {showTemplates ? "Sluit" : "Open"} templates
                  </button>
                </div>
              </div>

              {/* Template list */}
              {showTemplates && (
                <div className="templates-list">
                  {templates.length === 0 ? (
                    <p className="templates-empty-message">Geen templates</p>
                  ) : (
                    <div className="user-templates-container">
                      <h3 className="user-templates-title">Mijn Templates</h3>
                      <div className="user-templates-list">
                        {templates.map((tpl, idx) => (
                          <div key={idx} className="default-template-item">
                            <div className="default-template-info">
                              <span className="default-template-name" title={tpl.name}>{tpl.name}</span>
                              <span className="default-template-segments-count">
                                {tpl.segments.length} {tpl.segments.length === 1 ? 'segment' : 'segments'}
                              </span>
                            </div>
                            <div className="default-template-buttons">
                              <button
                                className="default-template-use-btn"
                                onClick={() => handleLoadTemplate(tpl)}
                                disabled={mustSpin}
                                title="Gebruik deze template"
                              >
                                Laden
                              </button>
                              <button
                                className="default-template-delete-btn"
                                onClick={() => handleDeleteTemplate(idx)}
                                disabled={mustSpin}
                                title="Verwijder deze template"
                              >
                                Verwijderen
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {/* Default Templates */}
                  <DefaultTemplates onSelectTemplate={handleSelectDefaultTemplate} />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
